import { useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDocumentTitle } from '@/shared/hooks/use-document-title'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  ArrowLeft, Upload, Download, FileSpreadsheet,
  CheckCircle2, AlertTriangle, XCircle,
} from 'lucide-react'
import { useUploadExcel, useDescargarPlantilla } from '../hooks/useBdClientes'
import type { BulkUploadResult } from '../types'

export function BulkUploadPage() {
  useDocumentTitle('Carga Masiva - BD Clientes')
  const navigate = useNavigate()

  const [dragActive, setDragActive] = useState(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [result, setResult] = useState<BulkUploadResult | null>(null)

  const uploadMutation = useUploadExcel()
  const plantillaMutation = useDescargarPlantilla()

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0]
      if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
        setSelectedFile(file)
        setResult(null)
      }
    }
  }, [])

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0])
      setResult(null)
    }
  }

  const handleUpload = async () => {
    if (!selectedFile) return
    const res = await uploadMutation.mutateAsync(selectedFile)
    setResult(res)
  }

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/bd-clientes')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Upload className="h-5 w-5 text-teal-500" />
          Carga Masiva de Clientes
        </h1>
      </div>

      {/* Instrucciones */}
      <Card className="border border-gray-300 dark:border-gray-700 bg-gradient-to-r from-teal-500/15 via-teal-500/5 via-50% to-transparent dark:from-teal-800/20 dark:via-teal-900/10 dark:via-50% dark:to-transparent">
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <FileSpreadsheet className="h-8 w-8 text-teal-500 flex-shrink-0" />
            <div className="space-y-2">
              <p className="font-medium">Instrucciones</p>
              <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
                <li>Descargue la plantilla Excel con el formato correcto</li>
                <li>Complete los datos de los clientes (los campos con * son obligatorios)</li>
                <li>Consulte la hoja "Referencia" para ver valores válidos de países, cargos, etc.</li>
                <li>Suba el archivo completado</li>
              </ol>
              <Button
                variant="outline"
                size="sm"
                onClick={() => plantillaMutation.mutate()}
                disabled={plantillaMutation.isPending}
                className="mt-2"
              >
                <Download className="mr-2 h-4 w-4" />
                Descargar Plantilla
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Drop zone */}
      <Card className="border border-gray-300 dark:border-gray-700">
        <CardContent className="pt-6">
          <div
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            className={`border-2 border-dashed rounded-lg p-12 text-center transition-colors ${
              dragActive
                ? 'border-teal-500 bg-teal-500/5'
                : 'border-gray-300 dark:border-gray-600'
            }`}
          >
            <Upload className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-lg font-medium mb-2">
              {selectedFile ? selectedFile.name : 'Arrastre un archivo Excel aquí'}
            </p>
            <p className="text-sm text-muted-foreground mb-4">
              {selectedFile
                ? `${(selectedFile.size / 1024).toFixed(1)} KB`
                : 'o haga clic para seleccionar (.xlsx)'}
            </p>
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileSelect}
              className="hidden"
              id="file-upload"
            />
            <div className="flex justify-center gap-2">
              <label htmlFor="file-upload">
                <Button variant="outline" asChild>
                  <span>Seleccionar Archivo</span>
                </Button>
              </label>
              {selectedFile && (
                <Button
                  onClick={handleUpload}
                  disabled={uploadMutation.isPending}
                  className="bg-teal-800 hover:bg-teal-900 text-white"
                >
                  {uploadMutation.isPending ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                      Procesando...
                    </>
                  ) : (
                    <>
                      <Upload className="mr-2 h-4 w-4" />
                      Subir y Procesar
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Resultados */}
      {result && (
        <Card className="border border-gray-300 dark:border-gray-700">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              {result.errores_count === 0 ? (
                <CheckCircle2 className="h-5 w-5 text-green-500" />
              ) : (
                <AlertTriangle className="h-5 w-5 text-yellow-500" />
              )}
              Resultados de la Carga
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-4 rounded-lg bg-gray-50 dark:bg-gray-800">
                <p className="text-2xl font-bold text-black dark:text-white">{result.total_filas}</p>
                <p className="text-sm text-muted-foreground">Total filas</p>
              </div>
              <div className="text-center p-4 rounded-lg bg-green-50 dark:bg-green-900/20">
                <p className="text-2xl font-bold text-green-600">{result.exitosos}</p>
                <p className="text-sm text-muted-foreground">Exitosos</p>
              </div>
              <div className="text-center p-4 rounded-lg bg-red-50 dark:bg-red-900/20">
                <p className="text-2xl font-bold text-red-600">{result.errores_count}</p>
                <p className="text-sm text-muted-foreground">Errores</p>
              </div>
            </div>

            {result.errores.length > 0 && (
              <div>
                <p className="font-medium mb-2 flex items-center gap-2">
                  <XCircle className="h-4 w-4 text-red-500" />
                  Detalle de errores
                </p>
                <div className="max-h-80 overflow-auto">
                  <Table>
                    <TableHeader className="bg-gray-100 dark:bg-[hsl(220,15%,16%)]">
                      <TableRow>
                        <TableHead className="w-20">Fila</TableHead>
                        <TableHead>Campo</TableHead>
                        <TableHead>Mensaje</TableHead>
                        <TableHead>Valor</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {result.errores.map((err, i) => (
                        <TableRow key={i}>
                          <TableCell>
                            <Badge variant="outline">{err.fila}</Badge>
                          </TableCell>
                          <TableCell className="font-medium">{err.campo}</TableCell>
                          <TableCell className="text-sm">{err.mensaje}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {err.valor || '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  )
}
